import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as occupancyTypesApi from './occupancy-types.api';

import type {
  CreateOccupancyTypeRequest,
  OccupancyTypeListQuery,
  UpdateOccupancyTypeRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: OccupancyTypeListQuery) => ['occupancy-types', 'list', query] as const,
  detail: (id: string) => ['occupancy-types', 'detail', id] as const,
};

export const useOccupancyTypesList = (query: OccupancyTypeListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => occupancyTypesApi.listOccupancyTypes(query),
    staleTime: 30_000,
  });

export const useCreateOccupancyType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOccupancyTypeRequest) =>
      occupancyTypesApi.createOccupancyType(payload),
    onSuccess: (ot) => {
      qc.invalidateQueries({ queryKey: ['occupancy-types', 'list'] });
      toast.success(`Added ${ot.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add occupancy type'),
  });
};

export const useUpdateOccupancyType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOccupancyTypeRequest }) =>
      occupancyTypesApi.updateOccupancyType(id, payload),
    onSuccess: (ot) => {
      qc.invalidateQueries({ queryKey: ['occupancy-types', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(ot.id) });
      toast.success('Occupancy type updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update occupancy type'),
  });
};

export const useDeleteOccupancyType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => occupancyTypesApi.deleteOccupancyType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['occupancy-types', 'list'] });
      toast.success('Occupancy type deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete occupancy type'),
  });
};

export const useBulkDeleteOccupancyTypes = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => occupancyTypesApi.bulkDeleteOccupancyTypes(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['occupancy-types'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete occupancy types'),
  });
};

export const useBulkSetOccupancyTypesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      occupancyTypesApi.bulkSetOccupancyTypesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['occupancy-types'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update occupancy type status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
